/**
 * ProCompareTable: the Basic vs Pro table, grouped under the owner's
 * headings (lib/whyProRows, which derives from lib/proFeatureRows). Rendered
 * by the Why go Pro sheet and by Settings > Why go Pro?, so the two can never
 * drift. Ticks and figures first, words as the fallback.
 */
import { Check, Crown, X } from "lucide-react";
import { WHY_PRO_GROUPS, type WhyProCell } from "@/lib/whyProRows";

export function ProCompareTable({
  testIdPrefix = "why-pro",
  className = "mt-4",
}: {
  testIdPrefix?: string;
  className?: string;
}) {
  const tid = (suffix: string) => `${testIdPrefix}-${suffix}`;
  return (
    <div
      className={`${className} rounded-2xl overflow-hidden`}
      style={{ border: "1px solid var(--border-subtle)" }}
      data-testid={tid("table")}
    >
      {/* Header: the two tiers. Pro carries the accent so the eye
          lands on the column that is the point of the sheet. */}
      <div
        className="grid items-center"
        style={{
          gridTemplateColumns: "1fr 64px 64px",
          background: "var(--surface-sunken)",
        }}
      >
        <div
          className="px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em]"
          style={{ color: "var(--text-ink-faint)" }}
        >
          Feature
        </div>
        <div
          className="py-2 text-center text-[11px] font-black uppercase tracking-wider"
          style={{ color: "var(--text-secondary)" }}
          data-testid={tid("col-basic")}
        >
          Basic
        </div>
        <div
          className="py-2 text-center text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1"
          style={{
            color: "var(--accent-fill)",
            background: "var(--accent-fill-glow-faint)",
          }}
          data-testid={tid("col-pro")}
        >
          <Crown className="w-3 h-3" aria-hidden />
          Pro
        </div>
      </div>

      {WHY_PRO_GROUPS.map((group) => (
        <div key={group.heading} data-testid={tid("group")}>
          <div
            className="px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em]"
            style={{
              color: "var(--accent-fill)",
              background: "var(--accent-fill-glow-faint)",
              borderTop: "1px solid var(--border-subtle)",
            }}
          >
            {group.heading}
          </div>
          {group.rows.map((row) => (
            <div
              key={row.id}
              className="grid items-center"
              data-testid={tid(`row-${row.id}`)}
              style={{
                gridTemplateColumns: "1fr 64px 64px",
                borderTop: "1px solid var(--border-subtle)",
              }}
            >
              <div className="px-3 py-2 leading-snug">
                <span
                  className="block text-[12px] font-semibold"
                  style={{ color: "var(--text-primary)" }}
                >
                  {row.label}
                </span>
                {row.detail && (
                  <span
                    className="block text-[10.5px] mt-0.5"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {row.detail}
                  </span>
                )}
              </div>
              <div
                className="py-2 flex items-center justify-center"
                data-testid={tid(`basic-${row.id}`)}
              >
                <Cell cell={row.basic} column="basic" />
              </div>
              <div
                className="py-2 h-full flex items-center justify-center"
                style={{ background: "var(--accent-fill-glow-faint)" }}
                data-testid={tid(`pro-${row.id}`)}
              >
                <Cell cell={row.pro} column="pro" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A tick, a cross, or a short figure. Pro ticks glow; Basic ticks stay quiet. */
function Cell({ cell, column }: { cell: WhyProCell; column: "basic" | "pro" }) {
  if (cell.kind === "yes") {
    return (
      <Check
        className="w-4 h-4"
        aria-label="Included"
        data-cell="yes"
        style={{
          color:
            column === "pro" ? "var(--accent-fill)" : "var(--text-secondary)",
        }}
      />
    );
  }
  if (cell.kind === "no") {
    return (
      <X
        className="w-4 h-4"
        aria-label="Not included"
        data-cell="no"
        style={{ color: "var(--text-tertiary)" }}
      />
    );
  }
  return (
    <span
      className="text-[11px] font-bold tabular-nums text-center leading-tight"
      data-cell="text"
      style={{
        color:
          column === "pro" ? "var(--accent-fill)" : "var(--text-secondary)",
      }}
    >
      {cell.label}
    </span>
  );
}
